import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { InfoOutlined, ErrorOutline, Spa } from '@mui/icons-material';
import type { AtterbergGeneral, AtterbergDetail } from '../../../types/atterberg';
import {
  applyOverrides,
  computeAtterbergResult,
  evaluateOrganicByRetest,
  ORGANIC_RETEST_RATIO,
} from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';

interface Props {
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
  retestLLTrials?: AtterbergDetail[];
}

const Stat = ({
  label,
  value,
  overridden,
  tooltip,
  big = false,
}: {
  label: string;
  value: string;
  overridden?: boolean;
  tooltip?: string;
  big?: boolean;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="top">
          <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
        </Tooltip>
      )}
      {overridden && (
        <Typography sx={{ fontSize: '0.6rem', color: 'warning.main', fontWeight: 700, ml: 0.25 }}>
          (override)
        </Typography>
      )}
    </Box>
    <Typography sx={{
      fontSize: big ? '1.4rem' : '1.05rem',
      fontWeight: 700,
      color: value === '—' ? 'text.disabled' : 'text.primary',
      lineHeight: 1.1,
    }}>
      {value}
    </Typography>
  </Box>
);

export const ResultsSummary = ({ general, llTrials, plTrials, retestLLTrials }: Props) => {
  const theme = useTheme();
  // Computed-value tooltips are sourced from the golden-linked Formula Registry.
  const { formulaFor } = useTestFormulas('atterberg');
  const computed = computeAtterbergResult({
    llTrials, plTrials, retestLLTrials,
    method: general.llMethod ?? 'Multipoint',
    isNonPlastic: general.isNonPlastic ?? false,
  });
  const final = applyOverrides(general, computed);

  const llStr = general.isNonPlastic ? 'NP' : (final.ll === null ? '—' : String(final.ll));
  const plStr = general.isNonPlastic ? 'NP' : (final.pl === null ? '—' : String(final.pl));
  const piStr = general.isNonPlastic ? 'NP' : (final.pi === null ? '—' : String(final.pi));
  const uscsStr = final.uscs ?? '—';

  const isNonPlastic = !!general.isNonPlastic;

  // FEAT-2026-0001: organic 75% rule status (only relevant when Organic is on)
  const organicOn = !!general.organic;
  const retestStatus = evaluateOrganicByRetest(computed.ll, computed.retestLL);
  const retestRatioPct = retestStatus.ratio === null ? null : retestStatus.ratio * 100;
  const retestThresholdPctLabel = `${(ORGANIC_RETEST_RATIO * 100).toFixed(0)}%`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, borderRadius: 2,
        border: `1px solid ${alpha(
          isNonPlastic ? theme.palette.warning.main : theme.palette.primary.main,
          0.4,
        )}`,
        backgroundColor: alpha(
          isNonPlastic ? theme.palette.warning.light : theme.palette.primary.light,
          0.06,
        ),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="overline" sx={{
          fontWeight: 700,
          color: isNonPlastic ? 'warning.dark' : 'primary.main',
          fontSize: '0.7rem', letterSpacing: 0.6,
        }}>
          Live Results
        </Typography>
        {isNonPlastic && (
          <Chip
            icon={<ErrorOutline sx={{ fontSize: 14 }} />}
            label="NON-PLASTIC"
            color="warning"
            size="small"
            sx={{
              height: 22, fontWeight: 800, fontSize: '0.68rem', letterSpacing: 0.5,
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mt: 1 }}>
        <Stat
          label="Liquid Limit"
          value={llStr}
          overridden={general.overrideLL !== null && general.overrideLL !== undefined}
          big
          tooltip={formulaFor('ll')?.formula}
        />
        <Stat
          label="Plastic Limit"
          value={plStr}
          overridden={general.overridePL !== null && general.overridePL !== undefined}
          big
          tooltip={formulaFor('pl')?.formula}
        />
        <Stat
          label="Plasticity Index"
          value={piStr}
          overridden={general.overridePI !== null && general.overridePI !== undefined}
          big
          tooltip={formulaFor('pi')?.formula}
        />
        <Stat
          label="USCS Group Symbol (D2487)"
          value={uscsStr}
          overridden={general.overrideUSCSGroupSymbol !== null
            && general.overrideUSCSGroupSymbol !== undefined
            && general.overrideUSCSGroupSymbol !== ''}
          big
        />
      </Box>
      {/* FEAT-2026-0001: organic 75% rule readout */}
      {organicOn && !isNonPlastic && (
        <Box
          sx={{
            mt: 1.25, pt: 1, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.7)}`,
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, alignItems: 'center',
          }}
        >
          <Stat
            label="Retest LL (oven-dried)"
            value={computed.retestLL === null ? '—' : String(computed.retestLL)}
            tooltip="Liquid Limit re-computed from the oven-dried retest trials."
          />
          <Stat
            label={`75% threshold (${retestThresholdPctLabel} × LL)`}
            value={retestStatus.threshold === null ? '—' : retestStatus.threshold.toFixed(1)}
            tooltip="ASTM D2487 organic-soil threshold: retest LL must be below this number to confirm organic."
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Spa
              sx={{
                fontSize: 18,
                color: retestStatus.status === 'organic'
                  ? 'success.main'
                  : retestStatus.status === 'not-organic'
                    ? 'text.disabled'
                    : 'warning.main',
              }}
            />
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                Organic per ASTM D2487
              </Typography>
              <Typography sx={{
                fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1,
                color: retestStatus.status === 'organic'
                  ? 'success.main'
                  : retestStatus.status === 'not-organic'
                    ? 'text.primary'
                    : 'warning.dark',
              }}>
                {retestStatus.status === 'organic'
                  ? `Yes · ratio ${retestRatioPct!.toFixed(1)}% < ${retestThresholdPctLabel}`
                  : retestStatus.status === 'not-organic'
                    ? `No · ratio ${retestRatioPct!.toFixed(1)}% ≥ ${retestThresholdPctLabel}`
                    : 'Pending retest data'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
